<script lang="ts">
  import SvelteSeo from 'svelte-seo';
  import { 
    Bug,
    CheckCircle2,
    FileText,
    Target,
    AlertTriangle,
    Code2,
    Clock,
    Search,
    BarChart3,
    Eye,
    Award,
    Info
  } from '@lucide/svelte';
  import { createPageSEO, createWebPageSchema, createBreadcrumbSchema, createFAQSchema } from '$lib/config/seo';
	import { Users } from '@lucide/svelte';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

  const faqs = [
    {
      question: "Can you really assess QA skill level from GitHub analysis?",
      answer: "Not definitively. We can detect red flags (hardcoded test data, brittle selectors, no page objects) and positive patterns (proper API mocking, good test organization). But we can't judge if someone is 'senior' or 'junior' - that requires interviews. Think of our analysis as filtering out obvious mismatches so you spend interview time on candidates who clear basic quality bars."
    },
    {
      question: "What can you actually detect with high confidence?",
      answer: "Binary patterns: Does code have cy.wait(numbers) vs cy.wait('@alias')? Are selectors using data-testid or nth-child? Is there a page object pattern or repeated selectors everywhere? Do test files exist? These are detectable. What we can't know: Is this appropriate for their context? Are they following team standards? Do they understand testing strategy?"
    },
    {
      question: "How accurate is your bug report analysis?",
      answer: "We examine public GitHub issues, but confidence is lower here. We can check if reproduction steps exist, if screenshots are attached, if the reporter provided environment details. But many good testers work in private issue trackers, and evaluating 'quality' is subjective. We show what we find and flag when data is limited."
    },
    {
      question: "What if someone's best testing work is in private repos?",
      answer: "This is common and we're upfront about it. Our analysis works best when testers have public test repositories. Without that, we show 'limited data available' and note lower confidence. We encourage candidates to share sanitized examples or create portfolio projects. When data is scarce, traditional screening is still necessary."
    },
    {
      question: "Do you replace technical interviews?",
      answer: "No. We help you avoid wasting interview time on candidates with obvious issues (no tests, terrible patterns, security mistakes). But determining if someone can think critically, find edge cases, and work with your team requires actual interviews and take-home exercises. We're pre-screening, not full assessment."
    },
    {
      question: "What about manual testing vs automation skills?",
      answer: "Automation is easier to analyze from code. Manual testing skills require reviewing bug reports, test case documentation, and ideally watching someone test. We show what we can detect from public repos (bug report quality, exploratory testing evidence in issues), but clearly mark confidence levels. Manual testing assessment is harder than automation analysis."
    }
  ];

  const seo = createPageSEO({
    title: 'Hire QA Testers - AI-Powered Test Code Analysis | TalentProfile',
    description: 'Find QA testers through AI analysis of test code quality, bug reports, and testing patterns. Combines static analysis + LLM code review for intelligent pre-screening.',
    canonical: '/hire-qa-tester',
    keywords: 'hire QA testers, test automation engineers, AI code review, Cypress developers, QA engineer recruitment',
    structuredData: [
      createWebPageSchema({
        name: 'Hire QA Testers',
        description: 'Pre-screen QA testers through automated analysis of test code patterns',
        url: '/hire-qa-tester'
      }),
      createBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Hire', url: '/hire' },
        { name: 'QA Testers' }
      ]),
      createFAQSchema(faqs)
    ]
  });

  const stats = [
    { value: "20+", label: "Quality Metrics", description: "Automated analysis + AI code review per repository" },
    { value: "3x", label: "Faster Screening", description: "Pre-qualified candidates vs manual resume review" },
    { value: "AI-Powered", label: "Analysis", description: "Static analysis + LLM contextual understanding" },
    { value: "24-48h", label: "Match Speed", description: "First analyzed candidates delivered" }
  ];

  const whatWeCanDetect = [
    {
      icon: AlertTriangle,
      category: "Automated Pattern Detection (High Confidence)",
      description: "Static analysis catches these definitively",
      checks: [
        "cy.wait(5000) vs cy.wait('@apiAlias') - AST parsing",
        "Brittle selectors: nth-child, complex CSS paths",
        "Hardcoded test data in files",
        "Missing data-testid attributes",
        "Security issues: exposed credentials",
        "No test files present",
        "CI/CD configuration missing"
      ],
      confidence: "HIGH"
    },
    {
      icon: Code2,
      category: "AI Code Review (Medium-High Confidence)",
      description: "LLM analyzes context, quality, and sophistication",
      checks: [
        "Page object pattern quality and maintainability",
        "Test organization appropriateness for codebase",
        "Edge case coverage comprehensiveness",
        "Bug report analytical depth and clarity",
        "Test data management strategy",
        "Error handling patterns and robustness",
        "Code evolution and improvement over time"
      ],
      confidence: "MEDIUM-HIGH"
    },
    {
      icon: Bug,
      category: "Pattern Recognition (Medium Confidence)",
      description: "Multiple signals combined for assessment",
      checks: [
        "Testing strategy sophistication (test pyramid balance)",
        "Exploratory testing evidence in issues",
        "Performance testing awareness",
        "Accessibility testing implementation"
      ],
      confidence: "MEDIUM"
    }
  ];

  const realTestExamples = [
    {
      title: "Selector Strategy (Easy to Detect)",
      junior: "cy.get('.container > div:nth-child(2) > button').click()",
      juniorIssue: "Brittle selector coupled to DOM structure - breaks with any layout change",
      senior: "cy.get('[data-testid=\"submit-button\"]').click()",
      seniorBenefit: "Stable selector that survives refactoring and styling changes",
      detectability: "HIGH - AST parsing catches selector patterns reliably"
    },
    {
      title: "API Testing (Easy to Detect)",
      junior: "cy.wait(3000); // wait for API\ncy.get('.user-name').should('contain', 'John');",
      juniorIssue: "Arbitrary timeout, no API verification, flaky in CI",
      senior: "cy.intercept('GET', '/api/user').as('getUser');\ncy.wait('@getUser');\ncy.get('[data-testid=\"user-name\"]').should('exist');",
      seniorBenefit: "Deterministic, verifies actual network call, catches API errors",
      detectability: "HIGH - cy.wait with numbers vs aliases is clear in code"
    },
    {
      title: "Page Object Pattern (Medium to Detect)",
      junior: "cy.get('#username').type('admin');\ncy.get('#password').type('pass');\ncy.get('button').click();",
      juniorIssue: "Repeated selectors across all login tests - change one, update everywhere",
      senior: "class LoginPage {\n  login(user, pass) {\n    cy.get('[data-testid=\"username\"]').type(user);\n    cy.get('[data-testid=\"password\"]').type(pass);\n    cy.get('[data-testid=\"login-btn\"]').click();\n  }\n}",
      seniorBenefit: "DRY principle, maintainable, clear abstraction",
      detectability: "MEDIUM - Can detect class/function existence, harder to judge quality"
    }
  ];

  const redFlagsWeCanCatch = [
    {
      pattern: "Arbitrary Waits",
      bad: "cy.wait(5000); // hope it loads",
      good: "cy.wait('@apiCall'); // deterministic",
      why: "Random waits cause flaky tests that waste CI time",
      detectable: true,
      method: "AST parsing for cy.wait with number literals"
    },
    {
      pattern: "Brittle Selectors",
      bad: ".container > div:nth-child(3) > span",
      good: "[data-testid='user-email']",
      why: "Breaks with any DOM restructuring or styling change",
      detectable: true,
      method: "Regex patterns for :nth-child, complex CSS paths"
    },
    {
      pattern: "No Page Objects",
      bad: "Selectors repeated in 20 different test files",
      good: "Selectors centralized in page object classes",
      why: "One UI change requires updating many test files",
      detectable: true,
      method: "Analyze import patterns and selector duplication"
    },
    {
      pattern: "Hardcoded Test Data",
      bad: "cy.type('test@example.com') in every test",
      good: "const USER = fixtures.validUser;",
      why: "Hard to maintain, unclear what data is significant",
      detectable: true,
      method: "Static analysis for string literals in test files"
    }
  ];

  const honestLimitations = [
    {
      icon: Code2,
      limitation: "Technical Pattern Assessment",
      reality: "AI can evaluate test code quality, page object sophistication, edge case coverage, and bug report analytical depth. LLM understands context like \"why this sleep() might be intentional\" or \"is this test organization appropriate for this codebase size.\"",
      whatWeDo: "Provide technical quality scores with confidence levels. Flag patterns worth discussing in interviews rather than making binary judgments."
    },
    {
      icon: Eye,
      limitation: "Data Availability",
      reality: "AI analysis works best with public repositories showing consistent patterns across multiple projects. Limited data means lower confidence scores, but even 1-2 repos with good code provide useful signals.",
      whatWeDo: "Clearly show data availability and confidence scores. Suggest traditional screening when public code is scarce."
    },
    {
      icon: Target,
      limitation: "Testing Strategy & Philosophy",
      reality: "AI can assess if tests exist and their technical quality, but understanding if someone thinks strategically about what to test, when to use E2E vs unit tests, or how to balance coverage requires dialogue.",
      whatWeDo: "Surface technical quality to filter candidates, then use interviews to probe testing philosophy and decision-making."
    },
    {
      icon: Users,
      limitation: "Soft Skills & Team Fit",
      reality: "AI cannot assess communication skills, collaboration ability, mentorship capacity, or cultural alignment. These human factors are critical for QA roles that involve working across teams.",
      whatWeDo: "Focus AI on technical screening to reduce interview pool. Use interviews to evaluate soft skills, problem-solving approach, and team dynamics."
    }
  ];

  const confidenceLevels = [
    {
      level: "High Confidence",
      color: "blue",
      icon: CheckCircle2,
      description: "Static analysis provides definitive detection",
      examples: [
        "Arbitrary waits vs proper API mocking",
        "Brittle selectors vs stable data-testids",
        "Hardcoded secrets in code",
        "Missing test files",
        "CI/CD configuration presence"
      ],
      useCase: "Filter out obvious technical issues"
    },
    {
      level: "Medium-High Confidence",
      color: "purple",
      icon: Award,
      description: "AI code review provides contextual assessment",
      examples: [
        "Page object pattern quality",
        "Test organization sophistication",
        "Edge case coverage depth",
        "Bug report analytical quality",
        "Code improvement trajectory"
      ],
      useCase: "Assess testing skill and sophistication"
    },
    {
      level: "Interview Required",
      color: "gray",
      icon: Users,
      description: "Human evaluation necessary",
      examples: [
        "Problem-solving approach",
        "Communication skills",
        "Team collaboration ability",
        "Domain knowledge depth",
        "Cultural fit"
      ],
      useCase: "Technical interviews remain essential"
    }
  ];

  const painPoints = [
    {
      icon: Clock,
      problem: "Wasting Interview Time on Obvious Mismatches",
      reality: "You spend 30 minutes with someone only to discover they don't know the difference between cy.wait(5000) and cy.wait('@apiCall')",
      solution: "Automated pre-screening catches these red flags before interviews",
      honest: "We can't tell you if they're 'senior', but we can tell you if they have glaring issues"
    },
    {
      icon: Target,
      problem: "No Way to Pre-Screen Test Code Quality",
      reality: "Resumes claim 'test automation expert' but you can't verify until technical interviews",
      solution: "Analyze public test repositories for common anti-patterns",
      honest: "We show what patterns exist in their code, you decide if they're concerning"
    },
    {
      icon: Bug,
      problem: "Can't Distinguish Tutorial Code from Real Work",
      reality: "Someone followed a Cypress tutorial vs built production test suites - resumes look identical",
      solution: "Check for sophistication indicators: page objects, proper API mocking, test organization",
      honest: "Multiple repos with consistent patterns are stronger signals than one polished project"
    }
  ];

  const whatYouGet = [
    {
      icon: Search,
      title: "Technical Quality Assessment",
      description: "AI evaluates test code sophistication and quality",
      includes: [
        "Automated pattern detection (waits, selectors)",
        "Code quality scoring with context",
        "Page object sophistication analysis",
        "Test organization evaluation",
        "Security issue detection"
      ],
      useCase: "Pre-screen technical competency before interviews"
    },
    {
      icon: Bug,
      title: "Testing Skill Indicators",
      description: "AI analyzes bug reports and testing approach",
      includes: [
        "Bug report analytical depth",
        "Edge case thinking patterns",
        "Exploratory testing evidence",
        "Test coverage strategy",
        "Quality improvement trajectory"
      ],
      useCase: "Assess testing sophistication and critical thinking"
    },
    {
      icon: BarChart3,
      title: "Confidence Scores",
      description: "Know what to trust and what needs verification",
      includes: [
        "High confidence: Binary detections",
        "Medium-high: AI contextual analysis",
        "Clear data availability indicators",
        "Multiple-repo pattern validation"
      ],
      useCase: "Make informed screening decisions"
    }
  ];

  const comparisonPoints = [
    {
      traditional: "Read resume claiming 'Cypress expert', schedule interview, discover basic misunderstanding",
      talentProfile: "See that their code has cy.wait(5000) everywhere - filter before interview",
      confidence: "HIGH"
    },
    {
      traditional: "Hope their tests are maintainable, find out after hiring they're brittle",
      talentProfile: "Check for page object pattern presence, repeated selectors, DRY violations",
      confidence: "MEDIUM"
    },
    {
      traditional: "Ask hypothetically 'how do you handle flaky tests?'",
      talentProfile: "Show them their code with arbitrary waits and ask 'why this approach?'",
      confidence: "HIGH"
    },
    {
      traditional: "Give take-home test (many candidates drop out)",
      talentProfile: "Review their existing test repos - they've already written tests",
      confidence: "MEDIUM"
    },
    {
      traditional: "Wonder if they write tests or just claim to",
      talentProfile: "See commit history, test files, but can't judge if they're the right tests",
      confidence: "LOW"
    }
  ];

  const testingTools = [
    {
      category: "E2E Testing",
      tools: "Cypress, Playwright, Selenium, Puppeteer",
      detectability: "HIGH - Framework imports and syntax patterns are clear"
    },
    {
      category: "Unit/Integration",
      tools: "Jest, Mocha, Vitest, Testing Library",
      detectability: "HIGH - Test runner configs and assertion patterns detectable"
    },
    {
      category: "API Testing",
      tools: "Supertest, REST Assured, Postman collections",
      detectability: "MEDIUM - Can see API test files, harder to assess quality"
    },
    {
      category: "CI/CD Integration",
      tools: "GitHub Actions, Jenkins, CircleCI configs",
      detectability: "HIGH - Config files are binary: present or absent"
    }
  ];
</script>

<SvelteSeo {...seo} />

<div class="min-h-[calc(100dvh-527px)] bg-white">
  <!-- Hero Section -->
  <section class="pt-20 pb-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Bug class="w-4 h-4" />
          QA Tester & Test Automation Engineer Recruitment
        </div>
        <h1 class="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Hire QA Testers Through<br />
          <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AI-Powered Code Analysis</span>
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          We combine static analysis with LLM code review to evaluate test quality, bug reports, and testing patterns. Screen candidates efficiently by analyzing their actual work before interviews.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="/drop-jd" class="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
            <FileText class="w-5 h-5" />
            Post Job Description
          </a>
          <a href="/how-it-works" class="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors border-2 border-gray-300">
            See Analysis Examples
          </a>
        </div>
      </div>

      <!-- Stats -->
      <div class="grid md:grid-cols-4 gap-6 mb-16">
        {#each stats as stat (stat.label)}
          <div class="bg-white p-6 rounded-xl border border-gray-200 text-center hover:shadow-lg transition-shadow">
            <div class="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">{stat.value}</div>
            <div class="text-sm font-semibold text-gray-900 mb-1">{stat.label}</div>
            <div class="text-xs text-gray-600">{stat.description}</div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- What We Can Actually Detect -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
        How <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AI Analysis</span> Evaluates Test Quality
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        We combine static code analysis with LLM-powered code review for contextual understanding. Here's what we can assess and with what confidence level.
      </p>

      <div class="space-y-6">
        {#each whatWeCanDetect as category (category.category)}
          {@const bgColor = category.confidence === 'HIGH' ? 'bg-green-50' : category.confidence === 'MEDIUM' ? 'bg-yellow-50' : 'bg-orange-50'}
          {@const borderColor = category.confidence === 'HIGH' ? 'border-green-200' : category.confidence === 'MEDIUM' ? 'border-yellow-200' : 'border-orange-200'}
          {@const badgeColor = category.confidence === 'HIGH' ? 'bg-green-600' : category.confidence === 'MEDIUM' ? 'bg-yellow-600' : 'bg-orange-600'}
          
          <div class="bg-white rounded-xl border-2 {borderColor} overflow-hidden">
            <div class="{bgColor} px-6 py-4 border-b {borderColor} flex items-center justify-between">
              <div class="flex items-center gap-3">
                <svelte:component this={category.icon} class="w-6 h-6 text-gray-700" />
                <div>
                  <h3 class="text-xl font-bold text-gray-900">{category.category}</h3>
                  <p class="text-sm text-gray-600">{category.description}</p>
                </div>
              </div>
              <span class="px-3 py-1 {badgeColor} text-white text-xs font-bold rounded-full">
                {category.confidence} CONFIDENCE
              </span>
            </div>
            <div class="p-6">
              <ul class="space-y-3">
                {#each category.checks as check (check)}
                  <li class="flex items-start gap-3">
                    <CheckCircle2 class="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                    <span class="text-gray-700">{check}</span>
                  </li>
                {/each}
              </ul>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Red Flags We Can Catch -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-4xl font-bold text-gray-900 mb-4 text-center">
        Red Flags We Can Reliably Detect
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        These are binary patterns: present or absent. No subjective judgment required.
      </p>

      <div class="grid md:grid-cols-2 gap-6">
        {#each redFlagsWeCanCatch as flag (flag.why)}
          <div class="bg-white rounded-xl border border-red-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div class="bg-red-50 px-6 py-4 border-b border-red-200">
              <h3 class="text-lg font-bold text-gray-900">{flag.pattern}</h3>
            </div>
            <div class="p-6 space-y-4">
              <div>
                <div class="text-xs font-bold text-red-600 mb-2">BAD PATTERN</div>
                <div class="bg-gray-900 rounded p-3">
                  <code class="text-red-400 text-sm font-mono">{flag.bad}</code>
                </div>
              </div>
              <div>
                <div class="text-xs font-bold text-green-600 mb-2">GOOD PATTERN</div>
                <div class="bg-gray-900 rounded p-3">
                  <code class="text-green-400 text-sm font-mono">{flag.good}</code>
                </div>
              </div>
              <div class="pt-3 border-t border-gray-200">
                <p class="text-sm text-gray-700 mb-2"><strong>Why it matters:</strong> {flag.why}</p>
                <p class="text-xs text-gray-500"><strong>Detection method:</strong> {flag.method}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Real Examples with Detectability -->
  <section class="py-16 px-4 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
    <div class="max-w-7xl mx-auto">
      <h2 class="text-4xl font-bold text-gray-900 mb-4 text-center">
        Real Pattern Detection Examples
      </h2>
      <p class="text-gray-600 text-center mb-16 max-w-2xl mx-auto">
        See what we can actually detect and how reliably
      </p>
      
      <div class="space-y-16">
        {#each realTestExamples as example (example.title)}
          <div class="relative">
            <div class="flex items-center justify-center mb-8">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-lg opacity-20"></div>
                <div class="relative bg-white px-8 py-3 rounded-full border-2 border-blue-200 shadow-lg">
                  <h3 class="text-xl font-bold text-gray-900">{example.title}</h3>
                </div>
              </div>
            </div>

            <div class="grid lg:grid-cols-2 gap-8 mb-6">
              <div class="group relative">
                <div class="absolute inset-0 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl blur-xl"></div>
                <div class="relative bg-white rounded-2xl border-2 border-red-200 overflow-hidden shadow-lg">
                  <div class="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-3 flex items-center justify-between">
                    <span class="text-white text-sm font-bold">ANTI-PATTERN</span>
                  </div>
                  <div class="bg-gray-900 p-6">
                    <pre class="text-sm overflow-x-auto"><code class="text-red-400 font-mono">{example.junior}</code></pre>
                  </div>
                  <div class="p-6 bg-red-50">
                    <div class="flex items-start gap-3">
                      <AlertTriangle class="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <p class="text-sm text-gray-700">{example.juniorIssue}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="group relative">
                <div class="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl blur-xl"></div>
                <div class="relative bg-white rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg">
                  <div class="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-3 flex items-center justify-between">
                    <span class="text-white text-sm font-bold">BETTER PATTERN</span>
                  </div>
                  <div class="bg-gray-900 p-6">
                    <pre class="text-sm overflow-x-auto"><code class="text-green-400 font-mono">{example.senior}</code></pre>
                  </div>
                  <div class="p-6 bg-green-50">
                    <div class="flex items-start gap-3">
                      <CheckCircle2 class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                      <p class="text-sm text-gray-700">{example.seniorBenefit}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
              <p class="text-sm text-gray-700">
                <strong>Detectability:</strong> {example.detectability}
              </p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Confidence Levels -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-4xl font-bold text-gray-900 mb-4 text-center">
        Our Confidence Level System
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        We tag every finding with confidence levels so you know what to trust
      </p>

      <div class="grid md:grid-cols-3 gap-8">
        {#each confidenceLevels as level (level.level)}
          {@const bgColor = level.color === 'green' ? 'from-green-500 to-emerald-600' : level.color === 'yellow' ? 'from-yellow-500 to-orange-500' : 'from-orange-500 to-red-500'}
          {@const bgLight = level.color === 'green' ? 'bg-green-50' : level.color === 'yellow' ? 'bg-yellow-50' : 'bg-orange-50'}
          
          <div class="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-lg">
            <div class="bg-gradient-to-br {bgColor} p-6 text-center">
              <div class="inline-flex p-3 bg-white/20 superellipse mb-3 backdrop-blur-sm">
                <svelte:component this={level.icon} class="w-8 h-8 text-white" />
              </div>
              <h3 class="text-2xl font-bold text-white mb-2">{level.level}</h3>
            </div>
            <div class="p-6 {bgLight} h-full">
              <p class="text-sm text-gray-700 mb-4 font-medium">{level.description}</p>
              <ul class="space-y-2 mb-4">
                {#each level.examples as example (example)}
                  <li class="text-sm text-gray-600 flex items-start gap-2">
                    <div class="w-1.5 h-1.5 superellipse bg-gray-400 mt-1.5 shrink-0"></div>
                    {example}
                  </li>
                {/each}
              </ul>
              <div class="pt-3 border-t border-gray-200">
                <p class="text-xs text-gray-600"><strong>Use case:</strong> {level.useCase}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>

       <div class="mx-auto max-w-4xl mt-24">
      <GlopayCtaHorizontal placement="hire-qa-tester-post-confidence-levels" />
    </div>
  </section>

  <!-- Analysis Scope -->
  <section class="py-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
        What AI Analysis <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Provides</span> and What Still Needs Interviews
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        AI code review is powerful for technical assessment, but human evaluation remains essential for complete hiring decisions
      </p>

      <div class="grid md:grid-cols-2 gap-8">
        {#each honestLimitations as item (item.limitation)}
          <div class="bg-white rounded-xl border-2 border-blue-200 overflow-hidden">
            <div class="bg-blue-50 px-6 py-4 border-b border-blue-200 flex items-center gap-3">
              <svelte:component this={item.icon} class="w-6 h-6 text-blue-700" />
              <h3 class="text-lg font-bold text-gray-900">{item.limitation}</h3>
            </div>
            <div class="p-6 space-y-4">
              <div>
                <p class="text-sm font-semibold text-gray-900 mb-1">What AI Can Do:</p>
                <p class="text-sm text-gray-700">{item.reality}</p>
              </div>
              <div class="pt-3 border-t border-gray-200">
                <p class="text-sm font-semibold text-gray-900 mb-1">Our Approach:</p>
                <p class="text-sm text-gray-700">{item.whatWeDo}</p>
              </div>
            </div>
          </div>
        {/each}
      </div>

      <div class="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
        <div class="flex items-start gap-4">
          <BarChart3 class="w-8 h-8 shrink-0" />
          <div>
            <h3 class="text-2xl font-bold mb-3">AI + Human: The Best Approach</h3>
            <p class="text-white/90 leading-relaxed mb-3">
              AI code review excels at technical pattern recognition, quality assessment, and catching issues at scale. It can evaluate test sophistication, bug report quality, and code evolution much faster than manual review.
            </p>
            <p class="text-white/90 leading-relaxed">
              But interviews remain essential for assessing problem-solving approach, communication skills, and team fit. Use our analysis to interview fewer candidates more effectively, not to skip interviews entirely.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Pain Points -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Problems We Actually Solve
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Time-saving pre-screening, not skill assessment
      </p>

      <div class="grid md:grid-cols-2 gap-8">
        {#each painPoints as point (point.problem)}
          <div class="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div class="flex items-start gap-4 mb-4">
              <div class="p-3 bg-red-100 superellipse shrink-0">
                <svelte:component this={point.icon} class="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 mb-2">{point.problem}</h3>
                <p class="text-gray-600 italic text-sm mb-3">"{point.reality}"</p>
              </div>
            </div>
            <div class="pl-16 space-y-3">
              <div class="pt-3 border-t border-gray-200">
                <div class="flex items-start gap-2 mb-2">
                  <CheckCircle2 class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <p class="text-sm text-gray-700"><strong>What we do:</strong> {point.solution}</p>
                </div>
                <div class="flex items-start gap-2">
                  <Info class="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <p class="text-sm text-blue-700"><strong>Being honest:</strong> {point.honest}</p>
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- What You Get -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
        What You Get with <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AI-Powered Analysis</span>
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        Comprehensive technical assessment with clear confidence indicators
      </p>

      <div class="grid md:grid-cols-3 gap-8">
        {#each whatYouGet as item (item.title)}
          <div class="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div class="p-3 bg-blue-100 superellipse w-fit mb-4">
              <svelte:component this={item.icon} class="w-6 h-6 text-blue-600" />
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
            <p class="text-gray-600 text-sm mb-4">{item.description}</p>
            <ul class="space-y-2 mb-4">
              {#each item.includes as include (include)}
                <li class="text-sm text-gray-700 flex items-start gap-2">
                  <div class="w-1.5 h-1.5 superellipse bg-blue-600 mt-1.5 shrink-0"></div>
                  {include}
                </li>
              {/each}
            </ul>
            <div class="pt-3 border-t border-gray-200">
              <p class="text-xs text-blue-700 font-medium">{item.useCase}</p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Comparison -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Traditional Screening vs Pattern Analysis
      </h2>

      <div class="space-y-6">
        {#each comparisonPoints as point (point.traditional)}
          {@const confidenceColor = point.confidence === 'HIGH' ? 'text-blue-700 bg-blue-100' : point.confidence === 'MEDIUM' ? 'text-purple-700 bg-purple-100' : 'text-gray-700 bg-gray-100'}
          
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div class="grid md:grid-cols-2 gap-6 p-6">
              <div class="flex items-start gap-3">
                <div class="w-6 h-6 superellipse bg-red-100 flex items-center justify-center shrink-0 mt-1">
                  <span class="text-xs text-red-600 font-bold">✕</span>
                </div>
                <p class="text-gray-600 text-sm">{point.traditional}</p>
              </div>
              <div class="flex items-start gap-3 md:border-l md:border-gray-200 md:pl-6">
                <div class="w-6 h-6 superellipse bg-green-100 flex items-center justify-center shrink-0 mt-1">
                  <CheckCircle2 class="w-4 h-4 text-green-600" />
                </div>
                <p class="text-gray-900 font-medium text-sm">{point.talentProfile}</p>
              </div>
            </div>
            <div class="px-6 pb-4">
              <span class="inline-block px-3 py-1 {confidenceColor} rounded-full text-xs font-bold">
                {point.confidence} CONFIDENCE
              </span>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- Tools Coverage -->
  <section class="py-16 px-4 bg-gray-50">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-4 text-center">
        Testing Tools We Can Analyze
      </h2>
      <p class="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
        With varying levels of confidence based on data availability
      </p>

      <div class="grid md:grid-cols-2 gap-6">
        {#each testingTools as tool (tool.category)}
          <div class="bg-white rounded-xl border border-gray-200 p-6">
            <h3 class="text-lg font-bold text-gray-900 mb-2">{tool.category}</h3>
            <p class="text-gray-700 text-sm mb-3">{tool.tools}</p>
            <div class="pt-3 border-t border-gray-200">
              <p class="text-xs text-gray-600">{tool.detectability}</p>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section class="py-16 px-4 bg-white">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
        Frequently Asked Questions
      </h2>
      <div class="space-y-6">
        {#each faqs as faq (faq.question)}
          <div class="bg-white p-6 rounded-xl border border-gray-200">
            <h3 class="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
            <p class="text-gray-700 leading-relaxed">{faq.answer}</p>
          </div>
        {/each}
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-20 px-4 bg-gray-50">
    <div class="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white shadow-xl">
      <h2 class="text-3xl md:text-4xl font-bold mb-6">
        Ready to Screen QA Testers More Effectively?
      </h2>
      <p class="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
        AI analysis evaluates test code quality, bug reports, and testing patterns. Get pre-screened candidates with technical assessments before interviews. Free forever.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="/drop-jd" class="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2">
          <FileText class="w-5 h-5" />
          Post Job Description
        </a>
        <a href="/hire" class="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors">
          Learn More
        </a>
      </div>
      <p class="text-white/75 mt-6 text-sm flex items-center justify-center gap-2">
        <Bug class="w-4 h-4" /> AI code review • Technical screening • No subscription fees
      </p>
    </div>
  </section>
</div>